import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import './verdian.css';
import VerdianIcon from '../../_assets/img/verdian-icon.png';

const LM_STUDIO_URL = 'https://verdian.sema.ce.gov.br';

const ENDPOINTS = {
    models: '/v1/models',
    nativeModels: '/api/v1/models',
    chat: '/v1/chat/completions',
    completions: '/v1/completions',
    embeddings: '/v1/embeddings'
};

const DEFAULT_CONTEXT_LENGTH = 8192;
const MAX_OUTPUT_TOKENS = 1024;
const CONTEXT_WARNING_PERCENT = 90;

const apiQwen = axios.create({
    baseURL: LM_STUDIO_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

const WELCOME_MESSAGE = {
    id: 'welcome',
    sender: 'ia',
    content:
        'Bem-vindo ao Verdian, inteligência artificial da Secretaria do Meio Ambiente e Mudança do Clima.'
};

const markdownComponents = {
    table: ({ node, ...props }) => (
        <div className="verdianChat__tableWrapper">
            <table {...props} />
        </div>
    ),

    a: ({ node, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer" />
    ),

    code: ({ node, className, children, ...props }) => {
        const text = String(children).replace(/\n$/, '');
        const isInline = !text.includes('\n');

        if (isInline) {
            return (
                <code className="verdianChat__inlineCode" {...props}>
                    {text}
                </code>
            );
        }

        return (
            <pre className="verdianChat__codeBlock">
                <code className={className} {...props}>
                    {text}
                </code>
            </pre>
        );
    }
};

export default function IAChatDialog() {
    const [visible, setVisible] = useState(false);
    const [messages, setMessages] = useState([WELCOME_MESSAGE]);
    const [inputMessage, setInputMessage] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);

    const [model, setModel] = useState('');
    const [modelInfo, setModelInfo] = useState(null);

    const [contextLength, setContextLength] = useState(DEFAULT_CONTEXT_LENGTH);

    const [estimatedTokens, setEstimatedTokens] = useState(0);
    const [contextBlocked, setContextBlocked] = useState(false);

    const toast = useRef(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!visible) {
            return;
        }

        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading, visible]);

    useEffect(() => {
        if (!visible) {
            return;
        }

        const timeout = setTimeout(() => {
            inputRef.current?.focus();
        }, 200);

        return () => clearTimeout(timeout);
    }, [visible]);

    const estimateTokens = text => {
        if (!text) {
            return 0;
        }

        const normalized = String(text).replace(/\s+/g, ' ').trim();

        if (!normalized) {
            return 0;
        }

        return Math.ceil(normalized.length / 4);
    };

    const estimateConversationTokens = conversation => {
        if (!Array.isArray(conversation)) {
            return 0;
        }

        let total = 0;

        conversation.forEach(item => {
            total += 4;

            if (typeof item.content === 'string') {
                total += estimateTokens(item.content);
            }

            if (Array.isArray(item.content)) {
                item.content.forEach(part => {
                    if (typeof part?.content === 'string') {
                        total += estimateTokens(part.content);
                    }

                    if (typeof part?.text === 'string') {
                        total += estimateTokens(part.text);
                    }
                });
            }
        });

        return total;
    };

    const getEffectiveContextLength = modelData => {
        const loadedContext =
            modelData?.loaded_instances?.[0]?.config?.context_length;

        if (typeof loadedContext === 'number' && loadedContext > 0) {
            return loadedContext;
        }

        if (
            typeof modelData?.max_context_length === 'number' &&
            modelData.max_context_length > 0
        ) {
            return modelData.max_context_length;
        }

        return DEFAULT_CONTEXT_LENGTH;
    };

    const loadModels = async () => {
        try {
            const response = await apiQwen.get(ENDPOINTS.models);

            const models = response?.data?.data || [];

            if (!models.length) {
                throw new Error('Nenhum modelo disponível.');
            }

            const selectedModel = models[0];

            const fallbackContext =
                selectedModel.context_length || DEFAULT_CONTEXT_LENGTH;

            setModel(selectedModel.id);

            setModelInfo({
                key: selectedModel.id,
                display_name: selectedModel.id,
                max_context_length: fallbackContext,
                loaded_instances: []
            });

            setContextLength(fallbackContext);
        } catch (error) {
            console.error('Erro ao consultar modelos:', error);
        }
    };

    useEffect(() => {
        if (!visible) {
            return;
        }

        loadModels();
    }, [visible]);

    const formatFileSize = bytes => {
        if (bytes < 1024) {
            return `${bytes} B`;
        }

        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }

        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const buildConversationMessages = currentMessage => {
        return [
            ...messages
                .filter(item => item.id !== 'welcome')
                .map(item => ({
                    role: item.sender === 'ia' ? 'assistant' : 'user',
                    content: item.content || ''
                })),
            {
                role: 'user',
                content: currentMessage
            }
        ];
    };

    const calculateContextPercentage = tokenCount => {
        if (!contextLength) {
            return 0;
        }

        return Math.min(
            100,
            Math.round((tokenCount / contextLength) * 100)
        );
    };

    const getContextStatus = percentage => {
        if (percentage >= 100) {
            return 'blocked';
        }

        if (percentage >= CONTEXT_WARNING_PERCENT) {
            return 'warning';
        }

        return 'normal';
    };

    useEffect(() => {
        const conversation = buildConversationMessages(inputMessage.trim());

        const tokens = estimateConversationTokens(conversation);

        setEstimatedTokens(tokens);

        setContextBlocked(tokens >= contextLength);
    }, [messages, inputMessage, contextLength]);

    const handleSelectFiles = event => {
        if (contextBlocked) {
            return;
        }

        const files = Array.from(event.target.files || []);

        if (!files.length) {
            return;
        }

        setAttachments(prev => [
            ...prev,
            ...files.map(file => ({
                id: `${file.name}-${file.lastModified}-${Math.random()}`,
                file
            }))
        ]);

        event.target.value = '';
    };

    const handleRemoveAttachment = id => {
        setAttachments(prev =>
            prev.filter(attachment => attachment.id !== id)
        );
    };

    const updateMessageContent = (messageId, updater) => {
        setMessages(prev =>
            prev.map(item =>
                item.id === messageId
                    ? {
                        ...item,
                        ...updater(item)
                    }
                    : item
            )
        );
    };

    const handleSendMessage = async () => {
        const message = inputMessage.trim();

        if (
            (!message && !attachments.length) ||
            loading ||
            contextBlocked
        ) {
            return;
        }

        if (!model) {
            console.error('Nenhum modelo foi encontrado no LM Studio.');

            return;
        }

        const conversationMessages = buildConversationMessages(message);

        const requestTokens = estimateConversationTokens(conversationMessages);

        if (requestTokens >= contextLength) {
            setContextBlocked(true);

            toast.current?.show({
                severity: 'error',
                summary: 'Limite de tokens atingido',
                detail: 'Não é possível enviar novas mensagens.',
                life: 5000
            });

            return;
        }

        const currentAttachments = attachments;

        const userMessage = {
            id: Date.now(),
            sender: 'user',
            content: message,
            files: currentAttachments.map(attachment => ({
                name: attachment.file.name,
                size: attachment.file.size
            }))
        };

        const iaMessageId = Date.now() + 1;

        setMessages(prev => [
            ...prev,
            userMessage,
            {
                id: iaMessageId,
                sender: 'ia',
                content: '',
                streaming: true
            }
        ]);

        setInputMessage('');
        setAttachments([]);
        setLoading(true);

        try {
            const response = await fetch(`${LM_STUDIO_URL}${ENDPOINTS.chat}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    model,
                    messages: conversationMessages,
                    temperature: 0.7,
                    max_tokens: MAX_OUTPUT_TOKENS,
                    stream: true
                })
            });

            if (!response.ok || !response.body) {
                let errorMessage = `HTTP ${response.status}`;

                try {
                    const errorData = await response.json();

                    errorMessage = errorData?.error?.message || errorMessage;
                } catch {
                    errorMessage = errorMessage;
                }

                throw new Error(errorMessage);
            }

            const reader = response.body.getReader();

            const decoder = new TextDecoder('utf-8');

            let buffer = '';
            let accumulated = '';

            while (true) {
                const { value, done } = await reader.read();

                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                const lines = buffer.split('\n');

                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmedLine = line.trim();

                    if (!trimmedLine || !trimmedLine.startsWith('data:')) {
                        continue;
                    }

                    const payload = trimmedLine.slice(5).trim();

                    if (payload === '[DONE]') {
                        continue;
                    }

                    try {
                        const parsed = JSON.parse(payload);

                        const delta =
                            parsed?.choices?.[0]?.delta?.content || '';

                        if (delta) {
                            accumulated += delta;

                            updateMessageContent(iaMessageId, () => ({
                                content: accumulated.trimStart()
                            }));
                        }
                    } catch {
                        continue;
                    }
                }
            }

            if (!accumulated.trim()) {
                updateMessageContent(iaMessageId, () => ({
                    content: 'Não foi possível obter uma resposta do Qwen.',
                    streaming: false
                }));
            } else {
                updateMessageContent(iaMessageId, () => ({
                    streaming: false
                }));
            }
        } catch (error) {
            console.error('Erro ao consultar Qwen:', error);

            const errorText = String(error?.message || '').toLowerCase();

            const isContextError =
                errorText.includes('context') ||
                errorText.includes('token') ||
                errorText.includes('maximum') ||
                errorText.includes('length');

            if (isContextError) {
                setContextBlocked(true);

                updateMessageContent(iaMessageId, () => ({
                    content: 'Limite de tokens atingido.',
                    streaming: false
                }));
            } else {
                updateMessageContent(iaMessageId, () => ({
                    content:
                        'Desculpe, ocorreu um erro ao tentar consultar a Verdian.',
                    streaming: false
                }));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = event => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();

            if (!contextBlocked) {
                handleSendMessage();
            }
        }
    };

    const handleClearChat = () => {
        setMessages([WELCOME_MESSAGE]);

        setAttachments([]);
        setInputMessage('');
        setEstimatedTokens(0);
        setContextBlocked(false);

        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    };

    const handleHide = () => {
        setVisible(false);
    };

    const contextPercentage = calculateContextPercentage(estimatedTokens);

    const contextStatus = getContextStatus(contextPercentage);

    const modelDisplayName =
        modelInfo?.display_name || modelInfo?.key || model || 'Modelo';

    const dialogHeader = (
        <div className="verdianChat__header">
            <div className="verdianChat__headerIcon">
                <img
                    src={VerdianIcon}
                    alt="Verdian"
                    className="verdianChat__headerLogo"
                />
            </div>

            <div className="verdianChat__headerContent">
                <h3>Verdian</h3>
                <span>Inteligência Artificial da SEMA</span>
            </div>

            <div className="verdianChat__headerActions">
                <Button
                    icon={<i className="fa-solid fa-trash" />}
                    text
                    rounded
                    severity="secondary"
                    onClick={handleClearChat}
                    tooltip="Limpar conversa"
                    tooltipOptions={{ position: 'bottom' }}
                />

                <Button
                    icon={<i className="fa-solid fa-xmark" />}
                    text
                    rounded
                    severity="secondary"
                    onClick={handleHide}
                    tooltip="Fechar"
                    tooltipOptions={{ position: 'bottom' }}
                />
            </div>
        </div>
    );

    return (
        <>
            <Toast ref={toast} className="verdianToast" />

            <Tooltip target=".verdianTrigger" position="left" />

            <Button
                className="verdianTrigger"
                icon={
                    <img
                        src={VerdianIcon}
                        alt="Verdian"
                        className="verdianChat__triggerIcon"
                    />
                }
                rounded
                onClick={() => setVisible(true)}
                data-pr-tooltip="Falar com a Verdian"
            />

            <Dialog
                visible={visible}
                onHide={handleHide}
                modal={false}
                draggable
                resizable={false}
                closable={false}
                header={dialogHeader}
                className="verdianDialog verdianDialog--floating"
                contentClassName="verdianDialog__content"
            >
                <div className="verdianChat">
                    {contextStatus === 'warning' && (
                        <div className="verdianChat__contextAlert verdianChat__contextAlert--warning">
                            <div className="verdianChat__contextAlertIcon">
                                <i className="fa-solid fa-circle-exclamation" />
                            </div>

                            <div className="verdianChat__contextAlertContent">
                                <span>Você está próximo do limite de tokens.</span>
                            </div>
                        </div>
                    )}

                    {contextStatus === 'blocked' && (
                        <div className="verdianChat__contextAlert verdianChat__contextAlert--blocked">
                            <div className="verdianChat__contextAlertIcon">
                                <i className="fa-solid fa-circle-exclamation" />
                            </div>

                            <div className="verdianChat__contextAlertContent">
                                <span>
                                    Limite de tokens atingido. O envio de mensagens está temporariamente bloqueado.
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="verdianChat__messages">
                        {messages.map(message => (
                            <div
                                key={message.id}
                                className={`verdianChat__message verdianChat__message--${message.sender}`}
                            >
                                <div className="verdianChat__avatar">
                                    {message.sender === 'ia' ? (
                                        <img
                                            src={VerdianIcon}
                                            alt="Verdian"
                                            className="verdianChat__avatarIcon"
                                        />
                                    ) : (
                                        <i className="fa-solid fa-user" />
                                    )}
                                </div>

                                <div
                                    className={`verdianChat__bubble${message.streaming ? ' verdianChat__bubble--streaming' : ''}`}
                                >
                                    <span className="verdianChat__sender">
                                        {message.sender === 'ia' ? 'Verdian' : 'Você'}
                                    </span>

                                    {message.sender === 'ia' ? (
                                        message.streaming && !message.content ? (
                                            <div className="verdianChat__typing">
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                        ) : (
                                            message.content && (
                                                <div className="verdianChat__markdown">
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={markdownComponents}
                                                    >
                                                        {message.content}
                                                    </ReactMarkdown>

                                                    {message.streaming && (
                                                        <span className="verdianChat__cursor" />
                                                    )}
                                                </div>
                                            )
                                        )
                                    ) : (
                                        message.content && (
                                            <p>{message.content}</p>
                                        )
                                    )}

                                    {message.files?.length > 0 && (
                                        <div className="verdianChat__attachments">
                                            {message.files.map(file => (
                                                <div
                                                    key={file.name}
                                                    className="verdianChat__attachment"
                                                >
                                                    <i className="fa-solid fa-paperclip" />

                                                    <span>{file.name}</span>

                                                    <small>
                                                        {formatFileSize(file.size)}
                                                    </small>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        <div ref={messagesEndRef} />
                    </div>

                    <div className="verdianChat__footer">
                        {attachments.length > 0 && (
                            <div className="verdianChat__pendingAttachments">
                                {attachments.map(attachment => (
                                    <div
                                        key={attachment.id}
                                        className="verdianChat__pendingAttachment"
                                    >
                                        <i className="fa-solid fa-paperclip" />

                                        <span>{attachment.file.name}</span>

                                        <small>
                                            {formatFileSize(attachment.file.size)}
                                        </small>

                                        <button
                                            type="button"
                                            className="verdianChat__pendingAttachmentRemove"
                                            onClick={() =>
                                                handleRemoveAttachment(attachment.id)
                                            }
                                            aria-label={`Remover ${attachment.file.name}`}
                                        >
                                            <i className="fa-solid fa-xmark" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="verdianChat__inputWrapper">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                hidden
                                onChange={handleSelectFiles}
                                disabled={loading}
                            />

                            <Button
                                icon={<i className="fa-solid fa-paperclip" />}
                                text
                                rounded
                                severity="secondary"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={loading}
                                tooltip="Anexar arquivo"
                                tooltipOptions={{ position: 'top' }}
                            />

                            <InputText
                                ref={inputRef}
                                value={inputMessage}
                                onChange={event =>
                                    setInputMessage(event.target.value)
                                }
                                onKeyDown={handleKeyDown}
                                placeholder="Digite sua pergunta..."
                                disabled={loading}
                            />

                            <Button
                                icon={<i className="fa-solid fa-paper-plane" />}
                                rounded
                                severity={contextBlocked ? 'secondary' : 'success'}
                                onClick={handleSendMessage}
                                disabled={
                                    (!inputMessage.trim() && !attachments.length) ||
                                    loading ||
                                    contextBlocked
                                }
                                loading={loading}
                                tooltip={
                                    contextBlocked
                                        ? 'Limite de tokens atingido'
                                        : 'Enviar'
                                }
                                tooltipOptions={{ position: 'top' }}
                            />
                        </div>

                        {contextBlocked ? (
                            <span className="verdianChat__hint verdianChat__hint--limit">
                                <i className="fa-solid fa-lock" />
                                Limite de tokens atingido
                            </span>
                        ) : (
                            <span className="verdianChat__hint">
                                Pressione Enter para enviar
                            </span>
                        )}
                    </div>
                </div>
            </Dialog>
        </>
    );
}